import {AbstractEntity} from "@/domain/entity/AbstractEntity.ts";
import type {RouteLocationAsRelative} from "vue-router";
import {type CardData, EntityAction} from "@/domain/type/entity.type.ts";
import {RouteEnum} from "@/router/RouteEnum.ts";
import {useEntitiesStore} from "@/stores/entities.store.ts";
import {RequestEntityState, RequestEntityStatus, type SourceDetails} from "@/domain/type/synchro.api.type.ts";
import type {SessionDetails} from "@/domain/type/session.type.ts";
import {Source} from "@/domain/type/source.type.ts";
import {SynchroType} from "@/domain/type/synchro.type.ts";

export class Session extends AbstractEntity {
  cardIcon = 'mdi-calendar-clock'
  left: SessionDetails = {
    course: "",
    idEva: "",
    startDate: '',
    endDate: '',
    link: '',
    label: '',
    hours: ''
  }
  right: SessionDetails = {
    course: "",
    idEva: "",
    startDate: '',
    endDate: '',
    link: '',
    label: '',
    hours: ''
  }

  constructor(
    uuid: string,
    leftFromApi: SourceDetails[],
    rightFromApi: SourceDetails[][],
    state: RequestEntityState,
    status: RequestEntityStatus
  ) {
    super(uuid, leftFromApi, rightFromApi, state, status);
    this.left = this.hydrateDetails(leftFromApi);
    this.right = this.hydrateDetails(rightFromApi[0] ?? []);
    this.leftLink = this.left.link;
    if (this.right.link === '') {
      if (import.meta.env.VITE_AMMON_BASE_URL_OLD === import.meta.env.VITE_AMMON_BASE_URL) {
        this.rightLink = import.meta.env.VITE_AMMON_BASE_URL_OLD + '/AMMON/GESFORM/Sessions/pages/SessionRecherche.aspx';
      } else {
        this.rightLink = import.meta.env.VITE_AMMON_BASE_URL + '/BO/s/GESFORM/Sessions/pages/SessionRecherche.aspx';
      }
    } else {
      this.rightLink = this.right.link;
    }
  }

  getCreateActionText(srcRight: Source): string {
    const target = srcRight.charAt(0).toUpperCase() + srcRight.slice(1).toLowerCase();
    return 'Êtes-vous certain de vouloir importer la séance dans ' + target + '?';
  }

  fusionLink(): RouteLocationAsRelative {
    return {
      name: RouteEnum.FusionFromOwner,
      params: {
        id: this.left.idEva,
        ownerId: useEntitiesStore().ownerId,
        sourceLeft: useEntitiesStore().sourceLeft,
        sourceRight: useEntitiesStore().sourceRight,
        type: SynchroType.SESSIONS
      }
    };
  }

  getActions(): EntityAction[] {
    if (useEntitiesStore().sourceLeft === Source.AMMON) {
      return [
        EntityAction.success,
        EntityAction.fusion
      ];
    }
    return [
      EntityAction.success,
      EntityAction.fusion,
      EntityAction.create,
    ];
  }

  getLeftCardData(): CardData[] {
    return this.getCardData(this.left)
  }

  getRightCardData(): CardData[] {
    return this.getCardData(this.right)
  }

  match(filter: string): boolean {
    return this.left.idEva.toLowerCase().includes(filter.toLowerCase())
      || this.left.startDate.toLowerCase().includes(filter.toLowerCase())
      || this.left.endDate.toLowerCase().includes(filter.toLowerCase());
  }

  protected hydrateDetails(sourceDetails: SourceDetails[]): SessionDetails {
    const details: SessionDetails = {
      course: '',
      idEva: '',
      startDate: '',
      endDate: '',
      label: '',
      link: '',
      hours: ''
    };

    sourceDetails.forEach((detail) => {
      switch (detail.label) {
        case '_id':
        case 'Référence action':
        case 'Id Eva':
        case 'Label':
          details.idEva = String(detail.val)
          break;
        case 'Lien':
          details.link = this.filterLink(String(detail.val));
          break;
        case 'Date de début':
          if (String(detail.val) === '') {
            break;
          }
          details.startDate = new Date(String(detail.val)).toLocaleDateString('fr-FR') + ' ' + new Date(String(detail.val)).toLocaleTimeString('fr-FR', {hour: '2-digit', minute: '2-digit'});
          break;
        case 'Date de fin':
          if (String(detail.val) === '') {
            break;
          }
          details.endDate = new Date(String(detail.val)).toLocaleDateString('fr-FR') + ' ' + new Date(String(detail.val)).toLocaleTimeString('fr-FR', {hour: '2-digit', minute: '2-digit'});
          break;
        case 'Nom':
          details.label = String(detail.val)
          break;
        case  'Nombre d\'heures':
        case  'Durée':
          details.hours = String(detail.val)
          break;
        // case 'Action de formation':
        //   details.course = String(detail.val)
        //   break;
        default:
          console.log(detail.label);
          console.log(detail.val);
        // console.warn(`Unknown label in session details: ${detail.label}`);
      }
    })

    return details
  }

  private getCardData(details: SessionDetails): CardData[] {
    return [
      {
        label: 'Date de début',
        value: details.startDate
      },
      {
        label: 'Date de fin',
        value: details.endDate
      },
      {
        label: 'Nombre d\'heures',
        value: details.hours
      }
    ];
  }
}
